import React, { useState, useEffect } from "react";
import { Box, Dialog, Typography } from "@mui/material";
import {
  getEditInvoiceResponse,
  getJDOpsCrops,
  getLandlordFieldTypes,
  saveInvoiceDetails,
} from "../../../slices/billingInvoices.slice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import CustomButton from "../../../components/Button";
import { RootState } from "../../../store";
import {
  AdhocProductsData,
  ApplicationOperationTableData,
  ApplicationProductTableData,
  CreditTableData,
  ErrorObj,
  HarvestTableData,
  Invoice,
  InvoiceData,
  PlantingTableData,
  SeedingTableData,
  TillageTableData,
} from "./types";
import { EditTable } from "./EditTable";
import {
  formatTableData,
  parseTableDate,
  validateAdhocProductData,
  validateCreditData,
  creditTableHeader,
} from "./util";
import { round } from "lodash";
import { EditableTitle } from "../../../components/EditableTitle/EditableTitle";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";

interface EditInvoiceModalProps {
  open: boolean;

  invoiceId: string;

  onClose: (value: boolean) => void;
  landlordId?: number;
}

const InvoiceGrid: React.FC<EditInvoiceModalProps> = ({
  open,
  invoiceId,
  onClose,
  landlordId,
}) => {
  const [invoice, setInvoice] = useState<Invoice>();

  const [loading, setLoading] = useState(true);

  const [applicationProductData, setApplicationProductData] = useState<
    ApplicationProductTableData[]
  >([]);

  const [applicationOperationData, setApplicationOperationData] = useState<
    ApplicationOperationTableData[]
  >([]);

  const [harvestData, setHarvestData] = useState<HarvestTableData[]>([]);
  const [tillageData, setTillageData] = useState<TillageTableData[]>([]);
  const [seedingData, setSeedingData] = useState<SeedingTableData[]>([]);
  const [plantingData, setPlantingData] = useState<PlantingTableData[]>([]);
  const [adhocProductsData, setAdhocProductsData] = useState<
    AdhocProductsData[]
  >([]);

  const [creditData, setCreditData] = useState<CreditTableData[]>([]);

  const [totalProductDue, setTotalProductDue] = useState<number>(0);
  const [totalOperationDue, setTotalOperationDue] = useState<number>(0);
  const [totalHarvestDue, setTotalHarvestDue] = useState<number>(0);
  const [totalTillageDue, setTotalTillageDue] = useState<number>(0);
  const [totalSeedingDue, setTotalSeedingDue] = useState<number>(0);
  const [totalPlantingDue, setTotalPlantingDue] = useState<number>(0);
  const [totalAdhocProductDue, setTotalAdhocProductDue] = useState<number>(0);
  const [totalCreditDue, setTotalCreditDue] = useState<number>(0);
  const [totalAmountDue, setTotalAmountDue] = useState<number>(0);

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isValid, setValid] = useState(false);
  const [errorFields, setErroFields] = useState<ErrorObj>({});
  const [editTitle, setEditTitle] = useState<string>("");

  const dispatch = useAppDispatch();

  const { invoiceObject } = useAppSelector(
    (state: RootState) => state.billingEntity
  );

  useEffect(() => {
    if (invoiceId) {
      dispatch(getEditInvoiceResponse(invoiceId));
    }
    setLoading(false);
    dispatch(getJDOpsCrops());
  }, [invoiceId]);

  useEffect(() => {
    if (invoiceObject?.invoiceId !== invoiceId) return;
    setInvoice(invoiceObject);
    if (invoiceObject?.invoiceData) {
      setEditTitle(invoiceObject.invoiceHeader.invoiceTitle);
      if (invoiceObject.isCustomBill) {
        invoiceObject?.invoiceData.forEach((item: any) => {
          if (item.type === "CustomBill") {
            setAdhocProductsData(
              parseTableDate(item.adhocProducts) as AdhocProductsData[]
            );
          } else if (item.type === "Adjustments and Credits") {
            setCreditData(
              parseTableDate(item.creditTableData) as CreditTableData[]
            );
          }
        });
      } else {
        invoiceObject?.invoiceData.forEach((item: any) => {
          if (item.type === "Application") {
            setApplicationProductData(
              parseTableDate(
                item.applicationProductTableData
              ) as ApplicationProductTableData[]
            );
            setApplicationOperationData(
              parseTableDate(
                item.applicationOperationTableData
              ) as ApplicationOperationTableData[]
            );
          } else if (item.type === "Harvest") {
            setHarvestData(
              parseTableDate(item.harvestTableData) as HarvestTableData[]
            );
          } else if (item.type === "Tillage") {
            setTillageData(
              parseTableDate(item.tillageTableData) as TillageTableData[]
            );
          } else if (item.type === "Seeding") {
            setSeedingData(
              parseTableDate(item.seedingTableData) as SeedingTableData[]
            );
            setPlantingData(
              parseTableDate(item.plantingTableData) as PlantingTableData[]
            );
          } else if (item.type === "Adjustments and Credits") {
            setCreditData(
              parseTableDate(item.creditTableData) as CreditTableData[]
            );
          }
        });
      }
    }
  }, [invoiceObject]);

  useEffect(() => {
    const totalAmount =
      totalProductDue +
      totalOperationDue +
      totalHarvestDue +
      totalTillageDue +
      totalSeedingDue +
      totalPlantingDue +
      totalAdhocProductDue +
      totalCreditDue;
    setTotalAmountDue(round(totalAmount, 2));
  }, [
    totalProductDue,
    totalOperationDue,
    totalHarvestDue,
    totalTillageDue,
    totalSeedingDue,
    totalPlantingDue,
    totalAdhocProductDue,
    totalCreditDue,
  ]);

  useEffect(() => {
    const isDataValid =
      !Object.keys(errorFields).length ||
      Object.keys(errorFields).every(
        (section: string) => errorFields[section].length === 0
      );
    setValid(isDataValid);
  }, [errorFields]);

  useEffect(() => {
    if (landlordId) {
      dispatch(getLandlordFieldTypes({ id: [landlordId] }));
    }
  }, [landlordId]);

  const handleSave = () => {
    const existingCreditSection = invoice?.invoiceData.find(
      (s: InvoiceData) => s.type === "Adjustments and Credits"
    );
    const creditSectionData = {
      $type: existingCreditSection?.$type ?? "Adjustments and Credits",
      type: "Adjustments and Credits",
      creditTableHeader: existingCreditSection?.creditTableHeader ?? creditTableHeader,
      creditTableData: formatTableData(creditData),
      totalCreditDue,
    };
    const hasCreditSection = !!existingCreditSection;
    const updatedData = invoice?.invoiceData.map((section: InvoiceData) => {
      if (invoiceObject.isCustomBill) {
        if (section.type === "CustomBill") {
          return {
            ...section,
            adhocProducts: formatTableData(adhocProductsData),
            totalProductDue: totalAdhocProductDue,
          };
        } else if (section.type === "Adjustments and Credits") {
          return creditSectionData;
        }
      } else {
        if (section.type === "Application") {
          return {
            ...section,
            applicationProductTableData: formatTableData(
              applicationProductData
            ),
            applicationOperationTableData: formatTableData(
              applicationOperationData
            ),
            totalProductDue,
            totalOperationDue,
          };
        } else if (section.type === "Harvest") {
          return {
            ...section,
            harvestTableData: formatTableData(harvestData),
            totalHarvestDue,
          };
        } else if (section.type === "Tillage") {
          return {
            ...section,
            tillageTableData: formatTableData(tillageData),
            totalTillageDue,
          };
        } else if (section.type === "Seeding") {
          return {
            ...section,
            seedingTableData: formatTableData(seedingData),
            plantingTableData: formatTableData(plantingData),
            totalSeedingDue,
            totalPlantingDue,
          };
        } else if (section.type === "Adjustments and Credits") {
          return creditSectionData;
        }
      }
      return section;
    });

    const finalData = hasCreditSection
      ? updatedData
      : [...(updatedData ?? []), creditSectionData];

    dispatch(
      saveInvoiceDetails({
        ...invoice,
        invoiceHeader: { ...invoice?.invoiceHeader, invoiceTitle: editTitle },
        invoiceData: finalData,
        totalAmount: totalAmountDue,
      })
    ).then(() => {
      onClose(true);
    });
  };

  if (loading) return <div>Loading...</div>;

  if (!invoice) return <div>No invoice found.</div>;

  const handleCancel = (_event: React.SyntheticEvent, reason: string) => {
    if (reason === "backdropClick") {
      return;
    }
    onClose(false);
  };

  const handleSaveDialogClose = () => setShowSaveDialog(false);
  const handleCancelDialogClose = () => setShowCancelDialog(false);

  const handleSaveYes = () => {
    setShowSaveDialog(false);
    handleSave();
  };

  const handleCancelYes = () => {
    setShowCancelDialog(false);
    onClose(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="lg"
        fullWidth
        style={{ zIndex: 100 }}
      >
        <div style={{ padding: "24px" }}>
          {invoice.invoiceHeader && (
            <EditableTitle title={editTitle} setTitle={setEditTitle} />
          )}
          <Box sx={{ display: "flex", justifyContent: "right", gap: "1rem" }}>
            <CustomButton
              onClick={() => setShowSaveDialog(true)}
              sx={{ width: "110px" }}
              disabled={!isValid}
            >
              Save
            </CustomButton>
            <CustomButton
              variant="contained"
              onClick={() => setShowCancelDialog(true)}
            >
              {" "}
              Cancel{" "}
            </CustomButton>
          </Box>
          {invoice.invoiceData?.map((section: any, index: number) => {
            return (
            <div key={index} style={{ marginBottom: "40px" }}>
              <Typography variant="h6" style={{ marginBottom: "10px" }}>
                {section.type === "Adjustments and Credits" ? "Adjustments and Credits" : section.type}
              </Typography>

              {section.type === "Adjustments and Credits" && (
                <EditTable
                  sectionName="creditTableData"
                  headers={creditTableHeader}
                  tableData={creditData}
                  setTableData={setCreditData}
                  totalDue={totalCreditDue}
                  setTotalDue={setTotalCreditDue}
                  setErrorFieldsObj={setErroFields}
                  validateFn={validateCreditData}
                  totalLabel="Total Adjustments and Credits"
                  columnWidths={{
                    fieldType: 180,
                    cropType: 160,
                    date: 150,
                    activity: 180,
                    reasonForCredit: 280,
                    totalDue: 150,
                  }}
                />
              )}

              {section.type === "CustomBill" && (
                <EditTable
                  sectionName="adhocProducts"
                  headers={section.adhocProductTableHeader}
                  tableData={adhocProductsData}
                  setTableData={setAdhocProductsData}
                  totalDue={totalAdhocProductDue}
                  setTotalDue={setTotalAdhocProductDue}
                  setErrorFieldsObj={setErroFields}
                  validateFn={validateAdhocProductData}
                />
              )}

              {section.type === "Application" &&
                section.applicationProductTableData && (
                  <EditTable
                    sectionName="applicationProductTableData"
                    headers={section.applicationProductTableHeader}
                    tableData={applicationProductData}
                    setTableData={setApplicationProductData}
                    totalDue={totalProductDue}
                    setTotalDue={setTotalProductDue}
                    setErrorFieldsObj={setErroFields}
                  />
                )}
              <Box mt={2}></Box>

              {section.type === "Application" && (
                <EditTable
                  sectionName="applicationOperationTableData"
                  headers={section.applicationOperationTableHeader}
                  tableData={applicationOperationData}
                  setTableData={setApplicationOperationData}
                  totalDue={totalOperationDue}
                  setTotalDue={setTotalOperationDue}
                  setErrorFieldsObj={setErroFields}
                />
              )}

              {section.type === "Harvest" && (
                <EditTable
                  sectionName="harvestTableData"
                  headers={section.harvestTableHeader}
                  tableData={harvestData}
                  setTableData={setHarvestData}
                  totalDue={totalHarvestDue}
                  setTotalDue={setTotalHarvestDue}
                  setErrorFieldsObj={setErroFields}
                />
              )}

              {section.type === "Tillage" && (
                <EditTable
                  sectionName="tillageTableData"
                  headers={section.tillageTableHeader}
                  tableData={tillageData}
                  setTableData={setTillageData}
                  totalDue={totalTillageDue}
                  setTotalDue={setTotalTillageDue}
                  setErrorFieldsObj={setErroFields}
                />
              )}

              {section.type === "Seeding" && (
                <EditTable
                  sectionName="seedingTableData"
                  headers={section.seedingTableHeader}
                  tableData={seedingData}
                  setTableData={setSeedingData}
                  totalDue={totalSeedingDue}
                  setTotalDue={setTotalSeedingDue}
                  setErrorFieldsObj={setErroFields}
                />
              )}
              <Box mt={2}></Box>
              {section.type === "Seeding" && (
                <EditTable
                  sectionName="plantingTableData"
                  headers={section.plantingTableHeader}
                  tableData={plantingData}
                  setTableData={setPlantingData}
                  totalDue={totalPlantingDue}
                  setTotalDue={setTotalPlantingDue}
                  setErrorFieldsObj={setErroFields}
                />
              )}
            </div>
          )})}

          <Box sx={{ display: "flex", justifyContent: "right", gap: "1rem" }}>
            <div style={{ marginTop: "20px", fontSize: "16px" }}>
              <strong> Total Amount: {totalAmountDue}</strong>
            </div>
          </Box>
        </div>
      </Dialog>

      {/* Save confirmation dialog */}
      <ConfirmationDialog
        open={showSaveDialog}
        title="Save"
        description="Do you want to save the changes ?"
        handleNo={handleSaveDialogClose}
        handleYes={handleSaveYes}
      />

      {/* Cancel confirmation dialog */}
      <ConfirmationDialog
        open={showCancelDialog}
        title="Warning"
        description="Are you sure you want to cancel? Any unsaved changes will be lost."
        handleNo={handleCancelDialogClose}
        handleYes={handleCancelYes}
      />
    </>
  );
};
export default InvoiceGrid;
